import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, useTheme, Stack, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ExpandIcon from '../assets/Expand.svg?react';
import CollapseIcon from '../assets/collapse.svg?react';
import RemarksDialog from "../components/TaskDetails/RemarksDialog";
import PMRemarkDialog from "../components/TaskDetails/PMRemarkDialog";
import {
  ActivateFlag,
  WorkerRemarkUpdate,
  PMRemarkStatusUpdate,
  AxiosResponse,
  GetObservationTaskDetailsApi
} from "../utils/urlConst";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import apiService from "../apiService/ApiService";
import CommonLoader from "../components/CommonLoader";
import { useAlert } from "../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import useResponsive from "../hooks/useResponsive";
import { ProjectDetailsField } from "../components/TaskDetails/ProjectDetailsField";
import {
  ProjectData,
  Tool,
  Material,
  Medewerker,
} from "../interfaces/TaskDetails";
import { MaterialCardField } from "../components/TaskDetails/MaterialCardField";
import { RemarksCard } from "../components/TaskDetails/RemarksCard";
import { useTimerContext } from "../contexts/TimerContext";
import DailyRemarkDialog from "../components/TaskDetails/DailyRemarkDialog";
import MapComponent from "../components/TaskDetails/MapComponent";
import { FaunaSpecies } from "../apiService/ObservationApiService";
import ObservationList from "../components/Observation/ObservationList";
import { Observation } from "../interfaces/Observation";
import observationApiService from "../apiService/ObservationApiService";
import offlineSyncService from '../services/OfflineSyncService';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { WeatherFieldSection, TaskWeatherData } from '../components/Observation/WeatherFieldSection';
import CommonButton from '../components/CommonButton';
import { SaveTaskWeatherDataApi } from '../utils/urlConst';

const TaskDetailFieldScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customerId = import.meta.env.VITE_APP_COMPANY_ID;

  // Get authToken and emailId from localStorage
  const authToken = localStorage.getItem('authToken') || '';
  const emailId = (localStorage.getItem('emailId') || '').replace(/^["']|["']$/g, '');
  const userId = LocalStorageUtil.getItem(storageKeys.userId, "");
  const userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
  const isFlexRole = LocalStorageUtil.getItem(storageKeys.isFlexRole, 0);

  const [openDialog, setOpenDialog] = useState(false);
  const [openProjectRemarksDialog, setOpenProjectRemarksDialog] = useState(false);
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [isFlag, setIsFlag] = useState<number | null>(null);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [tool, setTool] = useState<Tool[]>([]);
  const [workerRemark, setWorkerRemark] = useState("");
  const [workerRemarkCopy, setWorkerRemarkCopy] = useState("");
  const [pmRemarkStatus, setPMRemarkStatus] = useState<boolean | null>();
  const [medewerkers, setMedewerkers] = useState("");
  const { showAlert } = useAlert();
  const [isProjectPopup, setIsProjectPopup] = useState(false);
  const { selectedProjectForLog, isTimerOn, selectedProjectIdForNavigation } =
    useTimerContext();
  const { id } = useParams<{ id: string }>();

  const { isTablet, isMobile, isDesktop } = useResponsive();
  const [openDailyRemarkDialog, setOpenDailyRemarkDialog] = useState(false);
  const [dailyRemark, setDailyRemark] = useState("");
  const [dailyRemarkCopy, setDailyRemarkCopy] = useState("");
  const [pmDailyRemarkStatus, setPMDailyRemarkStatus] = useState<
    boolean | null
  >();

  // Observation state
  const [observations, setObservations] = useState<Observation[]>([]);
  const [observationsLoading, setObservationsLoading] = useState(false);

  // Edit mode state for observation selection
  const [editMode, setEditMode] = useState(false);
  const [selectedObservationId, setSelectedObservationId] = useState<number | null>(null);

  // Get offline sync state
  const { isSyncing } = useOfflineSync();

  // Weather data state
  const [apiWeatherData, setApiWeatherData] = useState<TaskWeatherData | null>(null);
  const [manualWeatherData, setManualWeatherData] = useState<TaskWeatherData | null>(null);
  const [savedManualWeatherData, setSavedManualWeatherData] = useState<TaskWeatherData | null>(null);
  const [weatherErrors, setWeatherErrors] = useState<{ temperature?: string; wind_speed?: string; weather_code?: string }>({});
  const [isWeatherSaving, setIsWeatherSaving] = useState(false);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);

  // Delay map mount by 1s so the browser finishes GC on the previous page before
  // starting to download tiles — prevents peak memory spike during reload
  useEffect(() => {
    const timer = setTimeout(() => setMapMounted(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Store taskId in localStorage when component loads
    if (id) {
      const numericTaskId = Number(id);
      LocalStorageUtil.setItem(storageKeys.taskId, numericTaskId);
    }
    // task details api call
    getObservationTaskDetails();

    // Listen for offline sync completion and refresh task details
    const handleSync = (result: { success: number; failed: number }) => {
      if (result.success > 0) {
        setTimeout(() => {
          getObservationTaskDetails();
        }, 1000);
      }
    };

    // Add sync listener using imported service
    offlineSyncService.addSyncListener(handleSync);

    return () => {
      offlineSyncService.removeSyncListener(handleSync);
    };
  }, []);

  // Call fetchObservations only when projectData is set and not null
  useEffect(() => {
    if (projectData) {
      fetchObservations();
    } else {
      if (!isLoading) {
        setIsLoading(false);
      }
    }
  }, [projectData]);
  useEffect(() => {
    // Check the current project and project Selected for log are same
    if (
      (isTimerOn &&
        selectedProjectIdForNavigation !== selectedProjectForLog.id) ||
      !isTimerOn
    ) {
      setIsProjectPopup(true);
    }
  }, []);

  // Refresh observations when component comes back into focus (e.g., returning from observation screen)
  useEffect(() => {
    const handleFocus = () => {
      refreshObservations();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [id]);

  // Refresh observations when offline observations are synced
  useEffect(() => {
    const handleObservationsSynced = (event: any) => {
      refreshObservations();
    };

    window.addEventListener('observationsSynced', handleObservationsSynced);
    return () => {
      window.removeEventListener('observationsSynced', handleObservationsSynced);
    };
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  // Get the daily remarks if the remark_date matches today
  const todayRemark =
    projectData?.daily_remarks?.remark_date === today
      ? projectData?.daily_remarks
      : undefined;

  const noteText = todayRemark ? `${todayRemark.pm_remarks}` : null;

  const isLastTaskToComplete = (itemSelected: Material) => {
    if (projectData?.is_removal_task) {
      return true;
    } else {
      const isLast = projectData?.materials
        .filter(
          (item: Material) => itemSelected.material_id !== item.material_id
        )
        .reduce((result: boolean, item: Material) => {
          return item.material_status && result;
        }, true);
      return isLast;
    }
  };

  // worker updating remark
  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setWorkerRemark(event.target.value);
  };

  // Fetch API weather data from the weather service using project coordinates
  const fetchWeatherFromApi = async (data: any) => {
    try {
      const lat = data?.latitude != null
        ? (typeof data.latitude === 'string' ? parseFloat(data.latitude) : data.latitude)
        : null;
      const lng = data?.longitude != null
        ? (typeof data.longitude === 'string' ? parseFloat(data.longitude) : data.longitude)
        : null;

      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        const response = await observationApiService.fetchWeatherData(
          lat.toString(),
          lng.toString()
        );

        if (response && response.data) {
          const parseWeatherValue = (value: string): number | null => {
            if (!value) return null;
            const normalized = value.replace(',', '.');
            const parsed = parseFloat(normalized);
            return isNaN(parsed) ? null : parsed;
          };

          const apiData: TaskWeatherData = {
            temperature: parseWeatherValue(response.data.api_temperature),
            wind_speed: parseWeatherValue(response.data.api_wind_speed),
            wind_direction: response.data.api_wind_direction || null,
            weather_code: response.data.api_weather_code || null,
          };
          setApiWeatherData(apiData);
        }
      }
    } catch (error) {
      console.error('Error fetching weather data from API:', error);
    }
  };

  // This function retrieves task details from an API, processes the response,
  // and updates the state accordingly. It handles loading states, user authentication, and error
  // management while ensuring that the current user's
  // email is excluded from the list of task workers.
  const getObservationTaskDetails = async (retryCount = 0) => {
    try {
      setIsLoading(true);

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
        task_id: id,
        isFlexRole: isFlexRole
      });

      apiService.setAuthToken(userToken);

      const responseJson: AxiosResponse | null = await apiService.get(
        GetObservationTaskDetailsApi,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        if (!responseJson?.data || (responseJson?.data).length === 0) {
          showAlert(t("taskDetails.taskDoesNotExist"), "error");
          navigate("/planning");
          return;
        }

        // Check if essential project data is missing and retry if needed
        if (!responseJson?.data?.project_name && retryCount < 2) {
          setTimeout(() => {
            getObservationTaskDetails(retryCount + 1);
          }, 2000);
          return;
        }
        let emailId = LocalStorageUtil.getItem(storageKeys.emailId, "");

        const sortedTaskList = responseJson?.data?.medewerkers.filter(
          (item: Medewerker) => {
            return !item.email.toLowerCase().includes(emailId.toLowerCase());
          }
        );

        const workerLists = await sortedTaskList
          .map((item: Medewerker) => item?.voornaam)
          .join(", ");
        // Extract data for today's daily remark
        const today = new Date().toISOString().slice(0, 10);

        // Get the daily remarks if it exists
        const todayDailyRemark =
          responseJson?.data?.daily_remarks?.remark_date === today
            ? responseJson?.data?.daily_remarks
            : undefined;

        setMedewerkers(workerLists);
        setProjectData(responseJson?.data);
        console.log("Project data taskDetail", responseJson)

        // Load API weather data from task details response
        if (responseJson?.data) {
          // Always fetch API weather data from weather API using project coordinates
          fetchWeatherFromApi(responseJson.data);

          // Load manual weather data from task_weather_data field
          if (responseJson.data.task_weather_data) {
            const manualData: TaskWeatherData = {
              temperature: responseJson.data.task_weather_data.manual_temperature ?? null,
              wind_speed: responseJson.data.task_weather_data.manual_wind_speed ?? null,
              wind_direction: responseJson.data.task_weather_data.manual_wind_direction ?? null,
              weather_code: responseJson.data.task_weather_data.manual_weather_code ?? null,
            };
            setManualWeatherData(manualData);
            setSavedManualWeatherData(manualData);
          }
        }

        // Store project data in localStorage for ObservationScreen access
        try {
          localStorage.setItem('currentProjectData', JSON.stringify(responseJson?.data));
        } catch (e) {
          console.warn('Failed to store project data in localStorage:', e);
        }

        // setTool(responseJson?.data.tools);
        // setIsFlag(responseJson?.data.flag_status);
        setWorkerRemark(responseJson?.data.task_worker_remarks || "");
        setWorkerRemarkCopy(responseJson?.data.task_worker_remarks || "");
        setPMRemarkStatus(responseJson?.data.pm_remarks_status);
        setDailyRemark(todayDailyRemark?.worker_remarks || "");
        setDailyRemarkCopy(todayDailyRemark?.worker_remarks || "");
        setPMDailyRemarkStatus(
          todayDailyRemark?.pm_remarks_unread_status || null
        );

      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 2 || responseJson?.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
        setIsLoading(false);
      } else {
        showAlert(t("common.server_error"), "error");
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      showAlert(t("common.server_error"), "error");
      setIsLoading(false);
    }
    // Note: Don't set isLoading to false here - wait for observations to load
  };

  // Fetch observations for the current task
  const fetchObservations = async () => {
    try {
      setObservationsLoading(true);
      const observationsData = await observationApiService.getObservations(id);

      if (observationsData) {
        setObservations(observationsData);
      } else {
        setObservations([]);
      }
    } catch (error) {
      console.error('Error fetching observations:', error);
      setObservations([]);
    } finally {
      setObservationsLoading(false);
      // Hide the main loader after observations are loaded
      setIsLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    // Mark PM remarks as read when opening the dialog
    if (pmRemarkStatus) {
      pmRemarkStatusUpdate();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setWorkerRemark(workerRemarkCopy ?? "");
  };

  const handleSaveRemarks = async () => {
    await workerRemarkUpdate();
    handleCloseDialog();
  };

  const handleOpenDailyRemarkDialog = () => {
    setOpenDailyRemarkDialog(true);
    if (pmDailyRemarkStatus) {
      pmDailyRemarkStatusUpdate();
    }
  };

  const handleSaveDailyRemark = async () => {
    await dailyRemarkSave();
    handleCloseDailyRemarkDialog();
  };

  const handleCloseDailyRemarkDialog = () => {
    setOpenDailyRemarkDialog(false);
    setDailyRemark(dailyRemarkCopy ?? "");
  };

  const handleOpenProjectRemarksDialog = () => {
    setOpenProjectRemarksDialog(true);
  };

  const handleCloseProjectRemarksDialog = () => {
    setOpenProjectRemarksDialog(false);
  };

  // Stable callback for observation click on map (replaces inline arrow)
  const handleObservationClick = useCallback((observationId: string, observationData: any, location: { lat: number; lng: number }) => {
    console.log('Observation clicked:', observationId, location);
  }, []);

  // Handle observation selection in edit mode
  const handleObservationSelect = (observationId: number) => {
    if (selectedObservationId === observationId) {
      // Deselect if clicking the same observation
      setSelectedObservationId(null);
    } else {
      setSelectedObservationId(observationId);
      // Auto-enable edit mode when selecting an observation
      setEditMode(true);
    }
  };

  // Handle observation location update from map
  const handleObservationLocationUpdate = useCallback(async (observationId: number, newLocation: { lat: number; lng: number }) => {
    try {
      // Create POINT geometry string for PostGIS
      const geom = `POINT(${newLocation.lng} ${newLocation.lat})`;

      // Update the observation in the backend
      const response = await observationApiService.updateObservationLocation(observationId, geom);

      if (response && (response.status_code === 1 || response.status === true)) {
        showAlert(t('observation.locationUpdateSuccess', 'Location updated successfully'), 'success');

        // Refresh observations to get updated data
        await refreshObservations();

        setEditMode(false);
        setSelectedObservationId(null);

        // Dispatch event to reload map
        window.dispatchEvent(new CustomEvent('reloadMap'));
      } else {
        showAlert(t('observation.locationUpdateFailed', 'Failed to update location'), 'error');
      }
    } catch (error) {
      console.error('Error updating observation location:', error);
      showAlert(t('observation.locationUpdateError', 'Error updating location'), 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAlert, t]);

  // Handle fauna species selection from map click
  const handleSpeciesSelect = useCallback((species: FaunaSpecies, location: { lat: number; lng: number }) => {
    // Navigate to observation screen with selected species and location
    navigate('/observation', {
      state: {
        species,
        location,
        taskId: id, // Pass task ID for association
        manualWeatherData: projectData?.manual_weather_data || null,
      },
    });
  }, [navigate, id]);

  // Refresh observations (can be called after new observation is added)
  const refreshObservations = () => {
    fetchObservations();
  };

  // This function updates worker remarks for a specific task by sending a POST request to the server.
  // It handles various server response statuses to provide appropriate feedback to the user,
  // including success messages and error handling for different scenarios.
  // The function also manages loading states and user authentication.
  const workerRemarkUpdate = async () => {
    setIsLoading(true);

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      worker_remarks: workerRemark,
      isFlexRole: isFlexRole,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        WorkerRemarkUpdate,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        showAlert(t("taskDetails.workerRemarkUpdated"), "success");
        // task details api call
        getObservationTaskDetails();
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 0
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 2
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.taskDoesNotExist"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 3
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.invalidWorkerRemarks"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 4
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.workerRemarksNotSaved"), "error");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // This function handles the status change of a flag by sending a POST request to the server.
  // It retrieves the user ID and token from local storage, constructs a JSON payload,
  // and processes the server's response to update the flag status or handle errors accordingly.
  // Loading state is managed to provide feedback during the operation.
  const onFlagStatusChange = async () => {
    setIsLoading(true);

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      project_id: projectData?.project_id,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        ActivateFlag,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        setIsFlag(1);
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 2 || responseJson?.status_code === 0)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // This function updates the PM remark status by making an API call.
  // It retrieves the user ID and token from local storage, constructs a JSON payload,
  // and sends a POST request to update the status. Based on the response, it either updates
  // the state, clears local storage and navigates to the login page, or shows an error alert.
  // Loading state is managed throughout the process.
  const pmRemarkStatusUpdate = async () => {

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      isFlexRole: isFlexRole,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        PMRemarkStatusUpdate,
        jsondata
      );

      //check for status condition.
      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        setPMRemarkStatus(false);
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 0 || responseJson?.status_code === 2)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    }
  };

  const dailyRemarkSave = async () => {
    setIsLoading(true);

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      type: "daily_remarks",
      remark_id: todayRemark?.id,
      worker_remarks: dailyRemark,
      isFlexRole: isFlexRole,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        WorkerRemarkUpdate,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        showAlert(t("taskDetails.notesUpdate"), "success");
        // task details api call
        getObservationTaskDetails();
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 0
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 2
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.taskDoesNotExist"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 3
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.invalidWorkerRemarks"), "error");
      } else if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 4
      ) {
        LocalStorageUtil.clear();
        showAlert(t("taskDetails.workerRemarksNotSaved"), "error");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const pmDailyRemarkStatusUpdate = async () => {

    let jsondata = JSON.stringify({
      token: userToken,
      user_id: userId,
      task_id: id,
      type: "daily_remarks",
      remark_id: todayRemark?.id,
      isFlexRole: isFlexRole,
    });

    try {
      const responseJson: AxiosResponse | null = await apiService.post(
        PMRemarkStatusUpdate,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        // handle success, e.g. refresh data or update state
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 0 || responseJson?.status_code === 2)
      ) {
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    }
  };

  // Handler for manual weather data changes with real-time per-field validation
  const handleManualWeatherChange = (data: Partial<TaskWeatherData>) => {
    setManualWeatherData(prev => ({
      temperature: data.temperature !== undefined ? data.temperature : (prev?.temperature ?? null),
      wind_speed: data.wind_speed !== undefined ? data.wind_speed : (prev?.wind_speed ?? null),
      wind_direction: data.wind_direction !== undefined ? data.wind_direction : (prev?.wind_direction ?? null),
      weather_code: data.weather_code !== undefined ? data.weather_code : (prev?.weather_code ?? null),
    }));
    setWeatherErrors(prev => {
      const next = { ...prev };
      if (data.temperature !== undefined) {
        const val = data.temperature;
        if (val === null || val === undefined || val === '' || val === '-' || (typeof val === 'string' && val.endsWith('.'))) {
          delete next.temperature;
        } else {
          const num = Number(val);
          if (isNaN(num)) {
            next.temperature = 'Alleen numerieke waarden zijn toegestaan';
          } else if (num < -50 || num > 60) {
            next.temperature = '-50°C tot 60°C';
          } else {
            delete next.temperature;
          }
        }
      }
      if (data.wind_speed !== undefined) {
        const val = data.wind_speed;
        if (val === null || val === undefined || val === '' || (typeof val === 'string' && val.endsWith('.'))) {
          delete next.wind_speed;
        } else {
          const num = Number(val);
          if (isNaN(num)) {
            next.wind_speed = 'Alleen numerieke waarden zijn toegestaan';
          } else if (num < 0) {
            next.wind_speed = 'Negatieve waarden zijn niet toegestaan';
          } else if (num > 300) {
            next.wind_speed = '<=300 km/h';
          } else {
            delete next.wind_speed;
          }
        }
      }
      if (data.weather_code !== undefined) {
        const val = data.weather_code;
        if (!val || /^[a-zA-Z\s]*$/.test(val as string)) {
          delete next.weather_code;
        } else {
          next.weather_code = 'Alleen letters en spaties zijn toegestaan';
        }
      }
      return next;
    });
  };

  // Validate manual weather fields
  const validateWeatherFields = (): boolean => {
    const newErrors: { temperature?: string; wind_speed?: string; weather_code?: string } = {};
    if (manualWeatherData?.temperature !== null && manualWeatherData?.temperature !== undefined && manualWeatherData?.temperature !== '') {
      const num = Number(manualWeatherData.temperature);
      if (isNaN(num)) {
        newErrors.temperature = 'Alleen numerieke waarden zijn toegestaan';
      } else if (num < -50 || num > 60) {
        newErrors.temperature = '-50°C tot 60°C';
      }
    }
    if (manualWeatherData?.wind_speed !== null && manualWeatherData?.wind_speed !== undefined && manualWeatherData?.wind_speed !== '') {
      const num = Number(manualWeatherData.wind_speed);
      if (isNaN(num)) {
        newErrors.wind_speed = 'Alleen numerieke waarden zijn toegestaan';
      } else if (num < 0) {
        newErrors.wind_speed = 'Negatieve waarden zijn niet toegestaan';
      } else if (num > 300) {
        newErrors.wind_speed = '<=300 km/h';
      }
    }
    if (manualWeatherData?.weather_code && !/^[a-zA-Z\s]*$/.test(manualWeatherData.weather_code)) {
      newErrors.weather_code = 'Alleen letters en spaties zijn toegestaan';
    }
    setWeatherErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save weather data to the API
  const handleSaveWeatherData = async () => {
    if (!validateWeatherFields()) return;

    setIsWeatherSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const payload = {
        task_id: projectData?.task_id || Number(id),
        project_id: projectData?.project_id,
        date: today,
        manual_weather_code: manualWeatherData?.weather_code || null,
        manual_temperature: manualWeatherData?.temperature !== null && manualWeatherData?.temperature !== undefined && manualWeatherData?.temperature !== '' ? String(manualWeatherData.temperature) : null,
        manual_wind_speed: manualWeatherData?.wind_speed !== null && manualWeatherData?.wind_speed !== undefined && manualWeatherData?.wind_speed !== '' ? String(manualWeatherData.wind_speed) : null,
        manual_wind_direction: manualWeatherData?.wind_direction || null,
        api_weather_code: apiWeatherData?.weather_code || null,
        api_temperature: apiWeatherData?.temperature !== null && apiWeatherData?.temperature !== undefined && apiWeatherData?.temperature !== '' ? String(apiWeatherData.temperature) : null,
        api_wind_speed: apiWeatherData?.wind_speed !== null && apiWeatherData?.wind_speed !== undefined && apiWeatherData?.wind_speed !== '' ? String(apiWeatherData.wind_speed) : null,
        api_wind_direction: apiWeatherData?.wind_direction || null,
      };

      apiService.setAuthToken(userToken);
      const response: any = await apiService.post(SaveTaskWeatherDataApi, payload);

      if (response && response.status_code === 1) {
        showAlert(t('taskDetails.weatherSaveSuccess'), 'success');
        setSavedManualWeatherData(manualWeatherData);
        // Refresh task details to get updated data
        getObservationTaskDetails();
      } else if (response?.status_code === 0 || response?.status_code === 2) {
        LocalStorageUtil.clear();
        navigate('/login');
      } else {
        showAlert(t('taskDetails.weatherSaveFailed'), 'error');
      }
    } catch (error) {
      console.error('Error saving weather data:', error);
      showAlert(t('common.server_error'), 'error');
    } finally {
      setIsWeatherSaving(false);
    }
  };

  const weatherSaveDisabled = isWeatherSaving || !(['temperature', 'wind_speed', 'wind_direction', 'weather_code'] as const).some(f => {
    const normalize = (v: any) => (v === null || v === undefined || v === '') ? '' : String(v);
    return normalize((manualWeatherData as any)?.[f]) !== normalize((savedManualWeatherData as any)?.[f]);
  });

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        my: "15px",
      }}
    >
      <CommonLoader isVisible={isLoading} />

      {/* Sync in Progress Overlay */}
      {isSyncing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <CommonLoader isVisible={true} text={t('observation.syncInProgress')} />
        </Box>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent={isMobile || isTablet ? "center" : "space-evenly"}
        sx={{
          width: "100%",
        }}
      >
        {/* Left panel */}
        <Box
          sx={{
            width: isMobile || isTablet ? "100%" : "47%",
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile || isTablet ? "center" : "flex-start",
            minHeight: !isMobile && !isTablet ? "50vh" : "auto", // Match map height for alignment
          }}
        >
          <Box
            sx={{
              width: isMobile || isTablet ? "90%" : "90%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              gutterBottom
              fontFamily={"Nunito Sans Regular700"}
              sx={{
                color: theme.palette.blueColors.main,
                fontSize: isMobile ? "20px" : "22px",
                height: "auto",
              }}
            >
              {"Waarneming" + " "}
            </Typography>
          </Box>

          {/* Left - Projectcard section */}
          <ProjectDetailsField
            projectData={projectData || null}
            tool={tool}
            medewerkers={medewerkers}
            isFlag={isFlag || null}
            onFlagStatusChange={() => onFlagStatusChange()}
            setIsLoading={() => setIsLoading(isLoading)}
            isFlexRole={isFlexRole}
            handleOpenRemarksDialog={handleOpenProjectRemarksDialog}
          />
          {/* Left - Material section */}
          <MaterialCardField />

          {/* Weather Sections - Accordion */}
          <Box sx={{ width: isMobile || isTablet ? "90%" : "90%", mt: 2 }}>
            <Accordion
              expanded={weatherExpanded}
              onChange={() => setWeatherExpanded(!weatherExpanded)}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={
                  weatherExpanded ? (
                    <CollapseIcon style={{ width: '50px', height: '50px', color: theme.palette.blueColors.main ?? theme.palette.primary.main }} />
                  ) : (
                    <ExpandIcon style={{ width: '50px', height: '50px', color: theme.palette.blueColors.main ?? theme.palette.primary.main }} />
                  )
                }
                sx={{
                  bgcolor: 'white',
                  px: 0,
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    order: 1,
                    marginLeft: 'auto',
                    marginRight: 0,
                  },
                  '& .MuiAccordionSummary-content': {
                    order: 0,
                    marginRight: 'auto',
                    margin: '12px 0',
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: "Nunito Sans Regular700",
                    fontWeight: 600,
                    color: theme.palette.blueColors.main ?? theme.palette.primary.main,
                  }}
                >
                  Weersomstandigheden
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                {/* API Weather Section (read-only) */}
                <WeatherFieldSection
                  type="api"
                  weatherData={apiWeatherData}
                />

                {/* Manual Weather Section (editable) */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <WeatherFieldSection
                    type="manual"
                    weatherData={manualWeatherData}
                    onWeatherChange={handleManualWeatherChange}
                    weatherErrors={weatherErrors}
                  />
                </Box>

                {/* Save Weather Button */}
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <CommonButton
                    translationKey="common.button_opslaan"
                    onClick={handleSaveWeatherData}
                    disabled={weatherSaveDisabled}
                    fullWidth={false}
                    sx={{
                      width: "105px",
                      fontSize: '13px',
                      backgroundColor: weatherSaveDisabled ? "grey.400" : "primary.main",
                      color: theme.palette.secondary.main,
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Observations List - Desktop and Tablet */}
          {!isMobile && (
            <ObservationList
              observations={observations}
              isLoading={observationsLoading}
              parentLoading={isLoading}
              height="40vh"
              editMode={editMode}
              selectedObservationId={selectedObservationId}
              onObservationSelect={handleObservationSelect}
            />
          )}

          {/* Left - Remarks section  edited.*/}
          <RemarksCard
            handleOpenDialog={handleOpenDialog}
            handleOpenDailyRemarkDialog={handleOpenDailyRemarkDialog}
            pmRemarkStatus={pmRemarkStatus}
            pmDailyRemarkStatus={pmDailyRemarkStatus}
          />
          {/* Left - PDF download section */}
          {/* {isDesktop && <DocumentCard projectData={projectData} />} */}
        </Box>

        {/* Right panel (Map) */}
        <Box
          sx={{
            width: isMobile || isTablet ? "100%" : "47%",
            display: "flex",
            justifyContent: "center",
            mt: isMobile ? "25px" : "40px",
            height: "50%",
          }}
        >
          {mapMounted ? (
            <MapComponent
              projectData={projectData}
              onSpeciesSelect={handleSpeciesSelect}
              projectId={projectData?.project_id}
              taskId={projectData?.task_id}
              layerId={projectData?.layer_id}
              authUsername={emailId}
              authToken={authToken}
              customerId={customerId}
              showWMSLayers={true}
              showWFSLayers={false}
              enableDynamicLayers={true}
              onObservationClick={handleObservationClick}
              enableObservationInteraction={true}
              editMode={editMode}
              selectedObservationId={selectedObservationId}
              onObservationLocationUpdate={handleObservationLocationUpdate}
            />
          ) : (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.100', borderRadius: 1 }} />
          )}

        </Box>
      </Stack>

      {/* Observations List - Mobile (after map) */}
      {isMobile && (
        <Box sx={{
          width: "100%",
          px: 2,
          mt: 3,
          display: "flex",
          justifyContent: "center"
        }}>
          <Box sx={{ width: "90%" }}>
            <ObservationList
              observations={observations}
              isLoading={observationsLoading}
              parentLoading={isLoading}
              editMode={editMode}
              selectedObservationId={selectedObservationId}
              onObservationSelect={handleObservationSelect}
            />
          </Box>
        </Box>
      )}

      <RemarksDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveRemarks}
        onChange={handleChange}
        initialRemarks={workerRemark}
        pmRemarkText={projectData?.task_remarks ?? null}
      />

      <DailyRemarkDialog
        open={openDailyRemarkDialog}
        onClose={handleCloseDailyRemarkDialog}
        onSave={handleSaveDailyRemark}
        onChange={(e) => setDailyRemark(e.target.value)}
        initialNotes={dailyRemark}
        noteText={noteText}
      />

      <PMRemarkDialog
        open={openProjectRemarksDialog}
        onClose={handleCloseProjectRemarksDialog}
        remarkText={projectData?.remarks ?? null}
      />

      {/* {!isLoading && (
        <TimerProjectSelectionModal
          handleClose={() => {
            setIsProjectPopup(false);
          }}
          isAnotherProject={
            selectedProjectForLog.id !== null &&
            selectedProjectForLog.id !== selectedProjectIdForNavigation
          }
          isVisible={isProjectPopup}
          isDropdownDiabled={true}
        />
      )} */}
    </Box>
  );
};

export default TaskDetailFieldScreen;
